import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Box, Link } from "@mui/material";
import { markdownProseSx } from "../../chat/markdownProseSx";
import {
  encodeCitation,
  parseCitations,
  replaceDecodedCitationTokens,
  stripCitationTokens,
} from "../../../features/narrative-citations/parseCitations";
import {
  type CanCite,
} from "../../../features/narrative-citations/renderCitedSegments";
import { rehypeSanitizeCitationTokens } from "../../../features/narrative-citations/rehypeSanitizeCitationTokens";
import { CitationAnchor } from "../../../features/narrative-citations/CitationAnchor";
import type {
  Citation,
  CitationGroup,
} from "../../../features/narrative-citations/types";

interface CitedMarkdownProps {
  content: string;
  /** Omit for a focusable tooltip-only citation with no click action. */
  onCite?: (group: CitationGroup) => void;
  canCite?: CanCite;
}

const PLACEHOLDER_SEPARATOR = "\u2063";

function randomPlaceholderNonce(): string | null {
  const values = new Uint32Array(4);
  if (!globalThis.crypto?.getRandomValues) return null;
  try {
    globalThis.crypto.getRandomValues(values);
    return Array.from(values, (value) => value.toString(16).padStart(8, "0")).join(
      "",
    );
  } catch {
    return null;
  }
}

function unusedPlaceholderPrefix(...contents: string[]): string | null {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const nonce = randomPlaceholderNonce();
    if (!nonce) return null;
    const prefix = `${PLACEHOLDER_SEPARATOR}KARDA-CITATION-${nonce}:`;
    if (!contents.some((content) => content.includes(prefix))) return prefix;
  }
  return null;
}

function restoreCitationTokens(
  text: string,
  citations: Citation[],
  placeholderPrefix: string,
): string {
  const pattern = new RegExp(
    `${placeholderPrefix}(\\d+)${PLACEHOLDER_SEPARATOR}`,
    "gu",
  );
  return text.replace(pattern, (placeholder, indexRaw: string) => {
    const citation = citations[Number(indexRaw)];
    return citation ? encodeCitation(citation) : placeholder;
  });
}

type ElementWithChildren = React.ReactElement<{
  children?: React.ReactNode;
}>;

interface MarkdownElementProps {
  children?: React.ReactNode;
  href?: string;
  node?: { tagName?: string };
}

function isCitationOpaqueElement(
  child: React.ReactElement<MarkdownElementProps>,
): boolean {
  const tagName = child.props.node?.tagName;
  return tagName === "a" || tagName === "code" || child.props.href !== undefined;
}

const BLOCK_TAGS = new Set([
  "blockquote",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "li",
  "ol",
  "p",
  "pre",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
]);

function markdownTagName(
  child: React.ReactElement<MarkdownElementProps>,
): string | undefined {
  if (typeof child.type === "string") return child.type;
  return child.props.node?.tagName;
}

function hasDescendantBlock(children: React.ReactNode): boolean {
  let found = false;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement<MarkdownElementProps>(child)) return;
    const tagName = markdownTagName(child);
    if (tagName && BLOCK_TAGS.has(tagName)) {
      found = true;
      return;
    }
    if (hasDescendantBlock(child.props.children)) found = true;
  });
  return found;
}

function markdownText(children: React.ReactNode): string {
  let text = "";
  React.Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += String(child);
    } else if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
      if (child.type === "br") text += "\n";
      else text += markdownText(child.props.children);
    }
  });
  return text;
}

/** Links and code display clean text but never become citation controls. */
function citationParsingText(
  children: React.ReactNode,
  citations: Citation[],
  placeholderPrefix: string,
): string {
  let text = "";
  React.Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += String(child);
      return;
    }
    if (!React.isValidElement<MarkdownElementProps>(child)) return;
    if (child.type === "br") {
      text += "\n";
      return;
    }
    if (isCitationOpaqueElement(child)) {
      text += stripCitationTokens(
        restoreCitationTokens(
          markdownText(child.props.children),
          citations,
          placeholderPrefix,
        ),
      );
      return;
    }
    text += citationParsingText(
      child.props.children,
      citations,
      placeholderPrefix,
    );
  });
  return text;
}

function cleanMarkdownChildren(
  children: React.ReactNode,
  citations: Citation[],
  placeholderPrefix: string,
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === "string") {
      return stripCitationTokens(
        restoreCitationTokens(
          stripCitationTokens(child),
          citations,
          placeholderPrefix,
        ),
      );
    }
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) {
      return child;
    }
    if (child.props.children === undefined) return child;
    return React.cloneElement(
      child,
      undefined,
      cleanMarkdownChildren(child.props.children, citations, placeholderPrefix),
    );
  });
}

/** Clean only this block's raw text, leaving descendant blocks to themselves. */
function cleanDirectTextChildren(
  children: React.ReactNode,
  citations: Citation[],
  placeholderPrefix: string,
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === "string") {
      return stripCitationTokens(
        restoreCitationTokens(
          stripCitationTokens(child),
          citations,
          placeholderPrefix,
        ),
      );
    }
    if (!React.isValidElement<MarkdownElementProps>(child)) return child;
    const tagName = markdownTagName(child);
    if (tagName && BLOCK_TAGS.has(tagName)) return child;
    if (child.props.children === undefined) return child;
    return React.cloneElement(
      child,
      undefined,
      cleanDirectTextChildren(
        child.props.children,
        citations,
        placeholderPrefix,
      ),
    );
  });
}

interface TextRange {
  start: number;
  end: number;
}

function interactiveTextRanges(children: React.ReactNode): TextRange[] {
  const ranges: TextRange[] = [];
  let offset = 0;

  const visit = (nodes: React.ReactNode): void => {
    React.Children.forEach(nodes, (child) => {
      if (typeof child === "string" || typeof child === "number") {
        offset += String(child).length;
        return;
      }
      if (!React.isValidElement<MarkdownElementProps>(child)) return;
      if (child.type === "br") {
        offset += 1;
        return;
      }
      const start = offset;
      visit(child.props.children);
      if (isCitationOpaqueElement(child) && offset > start) {
        ranges.push({ start, end: offset });
      }
    });
  };

  visit(children);
  return ranges;
}

function visibleLength(node: React.ReactNode): number {
  return markdownText(node).length;
}

/** Preserve inline Markdown wrappers while slicing by visible-text offsets. */
function sliceMarkdownChildren(
  children: React.ReactNode,
  start: number,
  end: number,
): React.ReactNode {
  if (start >= end) return null;
  let offset = 0;
  return React.Children.map(children, (child) => {
    const length = visibleLength(child);
    const childStart = offset;
    const childEnd = childStart + length;
    offset = childEnd;

    if (length === 0) {
      return start <= childStart && childStart < end ? child : null;
    }
    if (end <= childStart || start >= childEnd) return null;

    const localStart = Math.max(0, start - childStart);
    const localEnd = Math.min(length, end - childStart);
    if (typeof child === "string" || typeof child === "number") {
      return String(child).slice(localStart, localEnd);
    }
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) {
      return null;
    }
    if (child.type === "br") return child;
    if (localStart === 0 && localEnd === length) return child;
    return React.cloneElement(
      child as ElementWithChildren,
      undefined,
      sliceMarkdownChildren(child.props.children, localStart, localEnd),
    );
  });
}

function decorateBlockChildren(
  children: React.ReactNode,
  onCite: ((group: CitationGroup) => void) | undefined,
  keyBase: string,
  citations: Citation[],
  placeholderPrefix: string,
  canCite?: CanCite,
): React.ReactNode {
  // A parent list/block must not consume a child paragraph's placeholder or
  // wrap descendant block elements inside CitationAnchor's inline span.
  if (hasDescendantBlock(children)) {
    return cleanDirectTextChildren(children, citations, placeholderPrefix);
  }

  const restoredText = restoreCitationTokens(
    stripCitationTokens(
      citationParsingText(children, citations, placeholderPrefix),
    ),
    citations,
    placeholderPrefix,
  );
  const { cleanText, groups } = parseCitations(restoredText);
  const cleanChildren = cleanMarkdownChildren(
    children,
    citations,
    placeholderPrefix,
  );

  // Fail closed if an unexpected Markdown node cannot be mapped faithfully to
  // visible offsets. Clean prose/formatting is preferable to wrong attribution.
  if (groups.length === 0 || markdownText(cleanChildren) !== cleanText) {
    return cleanChildren;
  }

  const interactiveRanges = interactiveTextRanges(cleanChildren);
  if (
    groups.some((group) =>
      interactiveRanges.some(
        (range) =>
          group.anchorStart < range.end && group.anchorEnd > range.start,
      ),
    )
  ) {
    // Never nest a citation button around a link/code control. The prose stays
    // formatted and clean, but attribution fails closed for this block.
    return cleanChildren;
  }

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  [...groups]
    .sort((left, right) => left.anchorStart - right.anchorStart)
    .forEach((group, index) => {
      const start = Math.min(
        cleanText.length,
        Math.max(cursor, group.anchorStart),
      );
      const end = Math.min(
        cleanText.length,
        Math.max(start, group.anchorEnd),
      );
      if (start > cursor) {
        nodes.push(
          <React.Fragment key={`${keyBase}-plain-${index}`}>
            {sliceMarkdownChildren(cleanChildren, cursor, start)}
          </React.Fragment>,
        );
      }
      nodes.push(
        <CitationAnchor
          key={`${keyBase}-citation-${index}`}
          group={group}
          onCite={onCite}
          canCite={canCite}
        >
          {start < end
            ? sliceMarkdownChildren(cleanChildren, start, end)
            : "\u2197"}
        </CitationAnchor>,
      );
      cursor = end;
    });

  if (cursor < cleanText.length) {
    nodes.push(
      <React.Fragment key={`${keyBase}-plain-tail`}>
        {sliceMarkdownChildren(cleanChildren, cursor, cleanText.length)}
      </React.Fragment>,
    );
  }
  return nodes;
}

/** Strip citations in DOM contexts that must not contain nested controls. */
function cleanNonInteractiveChildren(
  children: React.ReactNode,
  citations: Citation[],
  placeholderPrefix: string,
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === "string") {
      return stripCitationTokens(
        restoreCitationTokens(child, citations, placeholderPrefix),
      );
    }
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) {
      return child;
    }
    if (child.props.children === undefined) return child;
    return React.cloneElement(child, undefined,
      cleanNonInteractiveChildren(
        child.props.children,
        citations,
        placeholderPrefix,
      ),
    );
  });
}

export const CitedMarkdown: React.FC<CitedMarkdownProps> = ({
  content,
  onCite,
  canCite,
}) => {
  // Extract private payloads before markdown parsing so punctuation inside a
  // source label or quoted evidence can never be interpreted as markdown and
  // split across React nodes. Only opaque, markdown-safe references pass into
  // react-markdown; the real token is restored inside plain text children.
  const { processedContent, citations, placeholderPrefix } = React.useMemo(
    () => {
      const normalizedContent = content.replace(/\\n/g, "\n");
      // The prefix is selected from outside the input's string set. This makes
      // placeholder restoration collision-safe even when evidence text itself
      // contains a string shaped like one of our internal references.
      const cleanContent = stripCitationTokens(normalizedContent);
      const prefix = unusedPlaceholderPrefix(normalizedContent, cleanContent);
      if (!prefix) {
        // Placeholder integrity is part of citation attribution. If secure
        // randomness is unavailable, fail closed to clean prose with no anchors.
        return {
          processedContent: cleanContent,
          citations: [],
          placeholderPrefix: "",
        };
      }
      const extracted: Citation[] = [];
      const markdown = replaceDecodedCitationTokens(
        normalizedContent,
        (citation, index) => {
          extracted.push(citation);
          return `${prefix}${index}${PLACEHOLDER_SEPARATOR}`;
        },
      );
      return {
        processedContent: markdown,
        citations: extracted,
        placeholderPrefix: prefix,
      };
    },
    [content],
  );

  const components = React.useMemo<Components>(() => {
    const decorate =
      (tag: string, key: string) =>
      ({
        node: _node,
        children,
        ...rest
      }: {
        node?: unknown;
        children?: React.ReactNode;
        [prop: string]: unknown;
      }) => {
        void _node;
        return React.createElement(
          tag,
          rest,
          decorateBlockChildren(
            children,
            onCite,
            key,
            citations,
            placeholderPrefix,
            canCite,
          ),
        );
      };

    const map = {
      p: decorate("p", "p"),
      li: decorate("li", "li"),
      td: decorate("td", "td"),
      th: decorate("th", "th"),
      h1: decorate("h1", "h1"),
      h2: decorate("h2", "h2"),
      h3: decorate("h3", "h3"),
      h4: decorate("h4", "h4"),
      h5: decorate("h5", "h5"),
      h6: decorate("h6", "h6"),
      a: ({
        href,
        children,
      }: {
        href?: string;
        children?: React.ReactNode;
      }) => (
        <Link href={href} target="_blank" rel="noopener noreferrer">
          {cleanNonInteractiveChildren(
            children,
            citations,
            placeholderPrefix,
          )}
        </Link>
      ),
      code: ({
        node: _node,
        children,
        ...rest
      }: {
        node?: unknown;
        children?: React.ReactNode;
        [prop: string]: unknown;
      }) => {
        void _node;
        return (
          <code {...rest}>
            {cleanNonInteractiveChildren(
              children,
              citations,
              placeholderPrefix,
            )}
          </code>
        );
      },
    };
    return map as unknown as Components;
  }, [canCite, citations, onCite, placeholderPrefix]);

  return (
    <Box sx={markdownProseSx}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitizeCitationTokens]}
        components={components}
      >
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
};
